import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
    children: ReactNode;
    fallbackComponent?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null
        };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({
            error,
            errorInfo
        });

        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }

        // Log to error reporting service here if needed
        console.error('Error caught by boundary:', error, errorInfo);
    }

    private handleRefresh = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    public render() {
        if (this.state.hasError) {
            if (this.props.fallbackComponent) {
                return this.props.fallbackComponent;
            }

            return (
                <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
                    <CardContent>
                        <Typography variant="h6" color="error" gutterBottom>
                            Something went wrong
                        </Typography>

                        <Typography variant="body1" gutterBottom>
                            An error occurred while loading this section. You can try:
                        </Typography>

                        <Box sx={{ my: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={this.handleRefresh}
                                sx={{ mr: 2 }}
                            >
                                Refresh
                            </Button>
                        </Box>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Error Details (Development Only):
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                        mt: 1,
                                        p: 2,
                                        backgroundColor: 'grey.100',
                                        borderRadius: 1,
                                        overflow: 'auto'
                                    }}
                                >
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;